import { authorizeRequest } from '@u/http/authorizeRequest'
import { apiOrigin, baseURL } from '@u/http/apiOrigin'
import { createHttpClient } from '@u/http/createHttpClient'
import store from '../store'
import Bugsnag from '@bugsnag/js'
import { helper } from '@u/helper'
import { validationSummary } from '@u/http/validationSummary'
import i18n from '@/i18n'

export { apiOrigin, baseURL }

/**
 * Handles every failed API call: toasts, validation errors into the auth
 * store, redirect on 401/419. Rejecting keeps the caller's `.catch`.
 * On 401/419 the call never settles: the page is leaving for /connect, and
 * resolving it with nothing would crash the callers' `.then`.
 *
 * The error is flagged `handledByApiClient`: the user has been told (or the
 * call was cancelled), so a store action may reject with it without the
 * global handlers (main.js) reporting it a second time.
 */
function handleResponseError(error) {
  error.handledByApiClient = true
  if (error.code === 'ERR_CANCELED') return Promise.reject(error)
  if (error.response) {
    const response_data = error.response.data
    switch (error.response.status) {
      case 400:
        if (response_data?.message) {
          helper.toastWarning(response_data.message)
        }
        return Promise.reject(error)
      // 419: the Sanctum session (or its CSRF token) expired.
      case 401:
      case 419:
        // Ends the local session too: otherwise isAuthenticated stays true
        // and the next visit to / tries the API again before /connect.
        store.commit('auth/setAuth', {
          auth: {},
          expiration: null,
          errors: response_data?.errors ?? {}
        })
        window.location.href = '/connect'
        return new Promise(() => {})
      case 403:
        helper.toastWarning(
          response_data?.errors?.permissions ??
            response_data?.errors?.roles ??
            "Vous n'êtes pas autorisé à effectuer cette action."
        )
        return Promise.reject(error)
      case 404:
      case 408:
        if (!error.config?.skipNotFoundToast) {
          helper.toastDanger(
            response_data?.errors?.message ??
              'la ressource que vous recherchez est inexistante'
          )
        }
        return Promise.reject(error)
      case 409:
        if (!error.config?.skipConflictToast && response_data?.message) {
          helper.toastWarning(response_data.message)
        }
        return Promise.reject(error)
      case 422:
        if (!error.config?.skipValidationToast) {
          const fields = validationSummary(response_data?.errors, (key) =>
            i18n.global.te(key) ? i18n.global.t(key) : null
          )
          helper.toastWarning(
            response_data?.message ??
              (fields
                ? `des erreurs sont présentes dans le formulaire : ${fields}`
                : 'des erreurs sont présentes dans le formulaire')
          )
        }
        store.commit('auth/setErrors', response_data?.errors ?? {})
        return Promise.reject(error)
      case 429:
        helper.toastWarning(
          response_data?.errors?.throttle ?? 'Trop de requêtes, réessayez.'
        )
        return Promise.reject(error)
      case 500:
        helper.toastDanger('Erreur serveur')
        Bugsnag.notify(error)
        return Promise.reject(error)
      default:
        Bugsnag.notify(error)
        return Promise.reject(error)
    }
  } else {
    helper.toastDanger('erreur serveur')
    return Promise.reject(error)
  }
}

const apiClient = createHttpClient({
  baseURL,
  onRequest: authorizeRequest,
  onResponseError: handleResponseError
})

export default apiClient
